import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { ArrowDownward, ArrowUpward, Search } from '@mui/icons-material'
import {
  Alert,
  Box,
  Checkbox,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Pagination,
  Skeleton,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material'

import { useAppDispatch } from '../../../hooks/storeHooks'
import usePage from '../../../hooks/usePage'
import pageService from '../../../services/PageService'
import {
  storeListedAnalysisSuggestion,
  useGetAnalysisSuggestionGamesQuery,
  useGetAnalysisSuggestionsQuery,
  useGetInternalOrganizationsByIdsQuery,
  useGetInternalUsersByUsernamesQuery,
  useSaveAnalysisSuggestionMutation,
} from '../../api/analysis-suggestions'
import { useGetInternalOrganizationUsersQuery } from '../../api/organizations'
import { AnalysisListDialog } from '../../components/AnalysisListDialog/AnalysisListDialog'
import { AnalysisSuggestionCard } from '../../features/analysis-suggestions/components/AnalysisSuggestionCard/AnalysisSuggestionCard'
import { AnalysisSuggestionCommentDialog } from '../../features/analysis-suggestions/components/AnalysisSuggestionCommentDialog/AnalysisSuggestionCommentDialog'
import { AnalysisSuggestionsSummary } from '../../features/analysis-suggestions/components/AnalysisSuggestionsSummary/AnalysisSuggestionsSummary'
import { GAMEREFINERY_ORGANIZATION_ID } from '../../features/analysis-suggestions/constants'
import {
  ANALYSIS_SUGGESTIONS_PER_PAGE,
  filterAnalysisSuggestionRows,
  getAnalysisSuggestionsSummary,
  getAnalystOptions,
  isChangeStored,
  sortAnalysisSuggestionRows,
  toAnalysisSuggestionRows,
  toAnalysisSuggestionWrite,
  toStoredComment,
  uniqueValues,
} from '../../features/analysis-suggestions/helpers/analysisSuggestionHelpers'
import { AnalysisSuggestionChange, AnalysisSuggestionRow, AnalysisSuggestionSort } from '../../features/analysis-suggestions/types/AnalysisSuggestion'
import { getCreatedAnalysisPath } from '../../features/analysis/helpers/analysisPaths'
import { useMutationFeedback } from '../../hooks/useMutationFeedback'

export const analysisSuggestionsPath = '/internal/analysis-suggestions'

const sortOptions = [
  { value: AnalysisSuggestionSort.Date, label: 'internal-analysts:analysis_suggestions_sort_date' },
  { value: AnalysisSuggestionSort.Organization, label: 'internal-analysts:analysis_suggestions_sort_organization' },
  { value: AnalysisSuggestionSort.User, label: 'internal-analysts:analysis_suggestions_sort_user' },
  { value: AnalysisSuggestionSort.Importance, label: 'internal-analysts:analysis_suggestions_sort_importance' },
  { value: AnalysisSuggestionSort.Game, label: 'internal-analysts:analysis_suggestions_sort_game' },
]

export const AnalysisSuggestionsPage: FC = () => {
  usePage(pageService.getInternalPageWithId('internal-analysis-suggestions'))
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const { notify, notifyError } = useMutationFeedback()

  const suggestionsQuery = useGetAnalysisSuggestionsQuery()
  const suggestions = suggestionsQuery.data
  const gameIds = useMemo(() => uniqueValues((suggestions ?? []).map(({ gameId }) => gameId)), [suggestions])
  const organizationIds = useMemo(() => uniqueValues((suggestions ?? []).map(({ requestingOrganization }) => requestingOrganization)), [suggestions])
  const usernames = useMemo(() => uniqueValues((suggestions ?? []).map(({ requestingUser }) => requestingUser)), [suggestions])
  const gamesQuery = useGetAnalysisSuggestionGamesQuery(gameIds, { skip: !gameIds.length })
  const organizationsQuery = useGetInternalOrganizationsByIdsQuery(organizationIds, { skip: !organizationIds.length })
  const usersQuery = useGetInternalUsersByUsernamesQuery(usernames, { skip: !usernames.length })
  const analystsQuery = useGetInternalOrganizationUsersQuery(GAMEREFINERY_ORGANIZATION_ID)
  const [saveSuggestion] = useSaveAnalysisSuggestionMutation()

  const [sort, setSort] = useState(AnalysisSuggestionSort.Date)
  const [descending, setDescending] = useState(true)
  const [text, setText] = useState('')
  const [showNew, setShowNew] = useState(true)
  const [showUpdate, setShowUpdate] = useState(true)
  const [showCompleted, setShowCompleted] = useState(false)
  const [page, setPage] = useState(1)
  const [pending, setPending] = useState<{ [id: string]: AnalysisSuggestionChange }>({})
  const [commentId, setCommentId] = useState<string>()
  const [analysesGameId, setAnalysesGameId] = useState<string>()
  const navigate = useNavigate()

  const analysts = useMemo(() => getAnalystOptions(analystsQuery.data), [analystsQuery.data])
  const rows = useMemo(
    () =>
      toAnalysisSuggestionRows(suggestions ?? [], {
        games: gamesQuery.data,
        organizations: organizationsQuery.data,
        users: usersQuery.data,
        analysts,
      }),
    [analysts, gamesQuery.data, organizationsQuery.data, suggestions, usersQuery.data]
  )
  const summary = useMemo(() => getAnalysisSuggestionsSummary(rows), [rows])
  const matchingRows = useMemo(() => filterAnalysisSuggestionRows(rows, { text, showNew, showUpdate }), [rows, showNew, showUpdate, text])
  const completedCount = useMemo(() => matchingRows.filter(({ suggestion }) => suggestion.completed).length, [matchingRows])
  // Filtered on the stored value, so a card stays in place until its change is stored.
  const shownRows = useMemo(
    () =>
      sortAnalysisSuggestionRows(
        matchingRows.filter(({ suggestion }) => suggestion.completed === showCompleted),
        sort,
        descending
      ),
    [descending, matchingRows, showCompleted, sort]
  )
  const pageCount = Math.max(1, Math.ceil(shownRows.length / ANALYSIS_SUGGESTIONS_PER_PAGE))
  const currentPage = Math.min(page, pageCount)
  const pageRows = shownRows.slice((currentPage - 1) * ANALYSIS_SUGGESTIONS_PER_PAGE, currentPage * ANALYSIS_SUGGESTIONS_PER_PAGE)

  useEffect(() => setPage(1), [text, showNew, showUpdate, showCompleted, sort, descending])

  const commentRow = useMemo(() => rows.find(({ suggestion }) => suggestion.id === commentId), [commentId, rows])
  const gameName = useCallback(
    (row: AnalysisSuggestionRow) => row.game?.name || t('internal-analysts:analysis_suggestions_unknown_game', { id: row.suggestion.gameId }),
    [t]
  )

  const save = useCallback(
    async (row: AnalysisSuggestionRow, change: AnalysisSuggestionChange) => {
      const { id } = row.suggestion
      const name = gameName(row)
      setPending((current) => ({ ...current, [id]: change }))
      try {
        const { stored, confirmed } = await saveSuggestion(toAnalysisSuggestionWrite(row.suggestion, change)).unwrap()
        if (stored) dispatch(storeListedAnalysisSuggestion(stored))
        const landed = isChangeStored(stored, change)
        notify(
          t(
            !landed
              ? 'internal-analysts:analysis_suggestions_not_stored'
              : confirmed
              ? 'internal-analysts:analysis_suggestions_saved'
              : 'internal-analysts:analysis_suggestions_saved_unconfirmed',
            { name }
          ),
          landed ? 'success' : 'error'
        )
        return landed
      } catch {
        notifyError(t('internal-analysts:analysis_suggestions_save_failed', { name }))
        return false
      } finally {
        setPending(({ [id]: _saved, ...rest }) => rest)
      }
    },
    [dispatch, gameName, notify, notifyError, saveSuggestion, t]
  )

  const handleAssign = useCallback((row: AnalysisSuggestionRow, email: string) => email && save(row, { assignedAnalyst: email }), [save])
  const handleCompletedChange = useCallback((row: AnalysisSuggestionRow, completed: boolean) => save(row, { completed }), [save])
  const handleCommentSave = useCallback(
    async (html: string) => {
      if (!commentRow) return
      if (await save(commentRow, { internalComment: toStoredComment(html) })) setCommentId(undefined)
    },
    [commentRow, save]
  )

  const isLoading = suggestionsQuery.isLoading || gamesQuery.isLoading || organizationsQuery.isLoading || usersQuery.isLoading || analystsQuery.isLoading
  const lookupFailed = organizationsQuery.isError || usersQuery.isError || analystsQuery.isError

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" mb={2}>
        {t('internal-analysts:analysis_suggestions_intro')}
      </Typography>

      {suggestionsQuery.isError || gamesQuery.isError ? (
        <Alert severity="error">{t('internal-analysts:analysis_suggestions_load_failed')}</Alert>
      ) : isLoading ? (
        <Stack spacing={2} data-testid="analysis-suggestions-loading">
          <Skeleton variant="rectangular" height={240} />
          <Skeleton variant="rectangular" height={160} />
          <Skeleton variant="rectangular" height={160} />
        </Stack>
      ) : (
        <Stack spacing={2}>
          {lookupFailed && <Alert severity="warning">{t('internal-analysts:analysis_suggestions_lookup_failed')}</Alert>}
          <AnalysisSuggestionsSummary summary={summary} />

          <TextField
            size="small"
            fullWidth
            value={text}
            onChange={(event) => setText(event.target.value)}
            placeholder={t('internal-analysts:analysis_suggestions_search')}
            inputProps={{ 'aria-label': t('internal-analysts:analysis_suggestions_search') }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }} flexWrap="wrap" useFlexGap>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <TextField
                select
                size="small"
                label={t('internal-analysts:analysis_suggestions_sort_by')}
                value={sort}
                onChange={(event) => setSort(event.target.value as AnalysisSuggestionSort)}
                sx={{ minWidth: 220 }}
              >
                {sortOptions.map(({ value, label }) => (
                  <MenuItem key={value} value={value}>
                    {t(label)}
                  </MenuItem>
                ))}
              </TextField>
              <Tooltip title={t(descending ? 'internal-analysts:analysis_suggestions_descending' : 'internal-analysts:analysis_suggestions_ascending')}>
                <IconButton
                  aria-label={t(descending ? 'internal-analysts:analysis_suggestions_descending' : 'internal-analysts:analysis_suggestions_ascending')}
                  onClick={() => setDescending((current) => !current)}
                >
                  {descending ? <ArrowDownward /> : <ArrowUpward />}
                </IconButton>
              </Tooltip>
            </Stack>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={showCompleted ? 'completed' : 'open'}
              onChange={(_event, value: string | null) => value && setShowCompleted(value === 'completed')}
              aria-label={t('internal-analysts:analysis_suggestions_status')}
            >
              <ToggleButton value="open">{t('internal-analysts:analysis_suggestions_open', { count: matchingRows.length - completedCount })}</ToggleButton>
              <ToggleButton value="completed">{t('internal-analysts:analysis_suggestions_completed_count', { count: completedCount })}</ToggleButton>
            </ToggleButtonGroup>
            <Stack direction="row">
              <FormControlLabel
                control={<Checkbox checked={showNew} onChange={(event) => setShowNew(event.target.checked)} />}
                label={t('internal-analysts:analysis_suggestions_show_new')}
              />
              <FormControlLabel
                control={<Checkbox checked={showUpdate} onChange={(event) => setShowUpdate(event.target.checked)} />}
                label={t('internal-analysts:analysis_suggestions_show_update')}
              />
            </Stack>
          </Stack>

          <Typography variant="body2" color="text.secondary">
            {shownRows.length
              ? t('internal-analysts:analysis_suggestions_showing', {
                  from: (currentPage - 1) * ANALYSIS_SUGGESTIONS_PER_PAGE + 1,
                  to: (currentPage - 1) * ANALYSIS_SUGGESTIONS_PER_PAGE + pageRows.length,
                  count: shownRows.length,
                })
              : t('internal-analysts:analysis_suggestions_none')}
          </Typography>

          {pageRows.map((row) => {
            const change = pending[row.suggestion.id]
            return (
              <AnalysisSuggestionCard
                key={row.suggestion.id}
                row={change ? { ...row, suggestion: { ...row.suggestion, ...change } } : row}
                analysts={analysts}
                saving={!!change}
                onAssign={handleAssign}
                onCompletedChange={handleCompletedChange}
                onComment={(commented) => setCommentId(commented.suggestion.id)}
                onViewAnalyses={(viewed) => setAnalysesGameId(viewed.suggestion.gameId)}
              />
            )
          })}

          {pageCount > 1 && (
            <Box display="flex" justifyContent="center">
              <Pagination count={pageCount} page={currentPage} onChange={(_event, value) => setPage(value)} color="primary" />
            </Box>
          )}
        </Stack>
      )}

      <AnalysisSuggestionCommentDialog
        open={!!commentRow}
        gameName={commentRow && gameName(commentRow)}
        comment={commentRow?.suggestion.internalComment}
        saving={!!commentRow && !!pending[commentRow.suggestion.id]}
        onClose={() => setCommentId(undefined)}
        onSave={handleCommentSave}
      />
      <AnalysisListDialog
        open={!!analysesGameId}
        gameId={analysesGameId}
        onClose={() => setAnalysesGameId(undefined)}
        onAnalysisCreated={(id, comparedMarketIso) => analysesGameId && navigate(getCreatedAnalysisPath(id, analysesGameId, comparedMarketIso))}
      />
    </Box>
  )
}
